/**
 * Uebersicht mit Gliederung (Design 4.5, Skizze region-uebersicht-v2):
 * - oben das Titelbild des Arbeitsbereichs mit Name und den Namen darunter,
 * - „Unsere Gemeinden“: Bildkacheln der Gemeinden im Arbeitsbereich mit Zahlen und Absprung
 *   (Uebersicht · Daten · Zwilling); Gemeinden einer VG tragen deren Schild,
 * - darunter die Verwaltungsgemeinschaften als schmale Eintraege.
 * Ohne Titelbild: ruhige Flaeche in der Hausfarbe mit dem Namen. Ein Klick wechselt den
 * Arbeitsbereich, der Pfad oben springt mit.
 */
import { ArrowRight, Pause, Play } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { childrenOf, heroUrl, subtreeOf, useOrganisations, vgLabel, type OrganisationNode } from '../lib/organisations.js';
import { useTenant } from '../lib/tenant.js';
import { setWorkspace, type Workspace } from '../lib/workspace.js';
import { HeroAnimation } from '../public/HeroAnimation.js';
import { HeroBackdrop } from '../public/PortalLayout.js';

import { shortName } from './WorkspacePath.js';

function Bild({ node, className }: { node: OrganisationNode; className: string }): JSX.Element {
  const src = heroUrl(node);
  return src ? (
    <img src={src} alt="" className={`${className} object-cover`} loading="lazy" />
  ) : (
    <div aria-hidden className={`${className} flex items-end bg-brand-500 p-4 font-display text-2xl font-bold text-white/90`}>
      {shortName(node)}
    </div>
  );
}

export function RegionHero({ ws }: { ws: Workspace }): JSX.Element {
  const { items } = useOrganisations();
  const tenant = useTenant();
  const ids = subtreeOf(items, ws.node.id);
  const below = ids
    .slice(1)
    .map((id) => items.find((o) => o.id === id))
    .filter((o): o is OrganisationNode => !!o && o.kind === 'gemeinde');
  // Bewegter Titel (Verwaltung › Erscheinungsbild): das Gebiet dieses Bereichs, ohne Foto darunter.
  const agsUnten = new Set(ids.flatMap((id) => items.find((o) => o.id === id)?.ags ?? []));
  const gebiete = tenant.gebiete.filter((g) => agsUnten.has(g.ags));
  const animated = tenant.heroAnimation && gebiete.length > 0;
  const credit = animated ? null : ws.node.hero?.credit;
  return (
    <section className="relative isolate overflow-hidden border-b border-border bg-brand-900" aria-label={ws.node.name}>
      {animated ? (
        <HeroBackdrop src={null} gebiete={gebiete} pauseClassName="bottom-4 right-4" />
      ) : (
        <>
          <Bild node={ws.node} className="absolute inset-0 -z-10 h-full w-full" />
          <div className="absolute inset-0 -z-10 bg-gradient-to-t from-black/70 via-black/30 to-transparent" aria-hidden />
        </>
      )}
      <div className={`mx-auto flex max-w-6xl flex-col justify-end px-4 pb-7 pt-16 text-white md:px-8 ${animated ? 'min-h-64' : 'min-h-56'}`}>
        <h2 className="font-display text-3xl font-bold tracking-tight drop-shadow-sm md:text-4xl">{ws.node.name}</h2>
        {below.length > 0 ? <p className="mt-1 text-base text-white/90">{below.map(shortName).join(' · ')}</p> : null}
      </div>
      {credit ? <p className="absolute bottom-1 right-2 text-[10px] text-white/70">Bild: {credit}</p> : null}
    </section>
  );
}

export function RegionTiles({ ws }: { ws: Workspace }): JSX.Element | null {
  const { items } = useOrganisations();
  const navigate = useNavigate();
  const tenant = useTenant();
  // Ein Knopf haelt alle Kacheln an (WCAG 2.2.2); bei „Bewegung reduzieren“ stehen sie ohnehin.
  const reduce = useMemo(() => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true, []);
  const [playing, setPlaying] = useState(true);
  const gebietOf = (o: OrganisationNode) => (tenant.heroAnimation ? tenant.gebiete.filter((x) => o.ags.includes(x.ags)) : []);
  const below = subtreeOf(items, ws.node.id)
    .slice(1)
    .map((id) => items.find((o) => o.id === id))
    .filter((o): o is OrganisationNode => !!o);
  const gemeinden = below.filter((o) => o.kind === 'gemeinde');
  const vgs = below.filter((o) => o.kind === 'vg');
  if (gemeinden.length === 0 && vgs.length === 0) return null;
  const byId = new Map(items.map((o) => [o.id, o]));
  const animated = gemeinden.some((g) => gebietOf(g).length > 0);
  const go = (id: string, to: string) => {
    setWorkspace(id);
    navigate(to);
  };

  return (
    <div className="space-y-8">
      {gemeinden.length > 0 ? (
        <section aria-labelledby="gemeinden-titel" className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <h2 id="gemeinden-titel" className="font-display text-2xl font-bold tracking-tight">
              Unsere Gemeinden
            </h2>
            {animated && !reduce ? (
              <button
                type="button"
                onClick={() => setPlaying((p) => !p)}
                className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                {playing ? <Pause className="h-3.5 w-3.5" aria-hidden /> : <Play className="h-3.5 w-3.5" aria-hidden />}
                {playing ? 'Animation anhalten' : 'Animation abspielen'}
              </button>
            ) : null}
          </div>
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {gemeinden.map((g) => {
              const parent = g.parent_id ? byId.get(g.parent_id) : undefined;
              const geb = gebietOf(g);
              return (
                <li key={g.id} className="overflow-hidden rounded-md border border-border bg-card">
                  <button type="button" onClick={() => go(g.id, '/uebersicht')} className="relative block w-full text-left" aria-label={`Übersicht ${g.name}`}>
                    {geb.length > 0 ? (
                      <div aria-hidden className="relative isolate flex h-36 w-full items-end overflow-hidden bg-gradient-to-br from-brand-900 to-[oklch(0.2_calc(var(--brand-chroma)*0.6)_var(--brand-hue))] p-4 font-display text-2xl font-bold text-white">
                        <HeroAnimation tile gebiete={geb} hue={tenant.brandHue} chroma={tenant.brandChroma} playing={playing} />
                        {shortName(g)}
                      </div>
                    ) : (
                      <Bild node={g} className="h-36 w-full" />
                    )}
                    {parent?.kind === 'vg' ? (
                      <span className="absolute left-2 top-2 rounded bg-white/90 px-2 py-0.5 text-xs font-medium text-foreground">{vgLabel(parent)}</span>
                    ) : null}
                  </button>
                  <div className="space-y-2 p-4">
                    <h3 className="font-display text-lg font-semibold">{shortName(g)}</h3>
                    <p className="text-sm text-muted-foreground">
                      {g.datasets.total === 1 ? '1 Datensatz' : `${g.datasets.total} Datensätze`}
                    </p>
                    <p className="flex gap-3 text-sm">
                      <button type="button" className="text-primary underline-offset-4 hover:underline" onClick={() => go(g.id, '/uebersicht')}>
                        Übersicht
                      </button>
                      <button type="button" className="text-primary underline-offset-4 hover:underline" onClick={() => go(g.id, '/catalog')}>
                        Daten
                      </button>
                      <button type="button" className="text-primary underline-offset-4 hover:underline" onClick={() => go(g.id, '/twin')}>
                        Zwilling
                      </button>
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}
      {vgs.length > 0 ? (
        <section aria-labelledby="vg-titel" className="space-y-3">
          <h2 id="vg-titel" className="font-display text-xl font-bold tracking-tight">
            Verwaltungsgemeinschaften
          </h2>
          <ul className="divide-y rounded-md border border-border bg-card">
            {vgs.map((v) => (
              <li key={v.id}>
                <button type="button" onClick={() => go(v.id, '/uebersicht')} className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted">
                  <span className="font-semibold">{v.name}</span>
                  <span className="min-w-0 flex-1 truncate text-sm text-muted-foreground">
                    {v.datasets.own === 1 ? '1 eigener Datensatz' : `${v.datasets.own} eigene Datensätze`}
                    {' · umfasst '}
                    {childrenOf(items, v.id).map(shortName).join(' und ')}
                  </span>
                  <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

/** Zahl fuer die Lage-Kachel „Datensätze“ im Arbeitsbereich. */
export function regionDatasetHint(ws: Workspace): string {
  const own = ws.node.datasets.own;
  return `davon ${own} bei ${shortName(ws.node)}`;
}

